import React, { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";

import SalesReportForm from "../component/Sales_Activity_Report";
import SalesReportList from "../component/SalesReportList";
import ProjectManagement from "../component/ProjectManagement";
import SalesReportDetail from "../component/SalesReportDetail";
import { designTokens } from "../theme";

type ReportView = "list" | "create" | "projects";

const isReportView = (value: string | null): value is ReportView =>
  value === "list" || value === "create" || value === "projects";

export default function SalesReports() {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const selectedView: ReportView = isReportView(params.get("view"))
    ? (params.get("view") as ReportView)
    : "list";

  const updateUrl = (view: ReportView) => {
    navigate(`/sales-reports?view=${view}`, { replace: true });
  };

  const detailId = params.get("id");
  const detailCustomerType =
    params.get("customer_type") === "existing" ? "existing" : "prospective";
  const returnTo = location.state?.returnTo as string | undefined;

  if (!isAuthenticated) {
    return null;
  }

  if (params.get("view") === "detail" && detailId) {
    return (
      <Box sx={{ minHeight: "100dvh", backgroundColor: "#F4F6F8" }}>
        <Toolbar />
        <Box sx={{ p: { xs: 1, md: 3 } }}>
          <SalesReportDetail
            reportId={detailId}
            customerType={detailCustomerType}
            onBack={() => navigate(returnTo ?? "/sales-reports?view=list", { replace: true })}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background:
          "radial-gradient(circle at top right, rgba(152,188,46,0.07), transparent 28%), #F4F6F8",
      }}
    >
      <Toolbar />

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1480, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(115deg, rgba(255,255,255,0.98), rgba(247,250,242,0.98) 62%, rgba(235,243,225,0.98))",
              border: `1px solid ${designTokens.border.default}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.35,
                pointerEvents: "none",
                backgroundImage:
                  "radial-gradient(rgba(93,116,62,0.14) 0.7px, transparent 0.7px)",
                backgroundSize: "15px 15px",
                maskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{
                position: "relative",
                zIndex: 1,
                p: { xs: 1.6, md: 2 },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 2,
                    color: designTokens.brand.dark,
                    backgroundColor: designTokens.overlay.brandSoft,
                    border: `1px solid ${designTokens.overlay.brandMedium}`,
                  }}
                >
                  <DescriptionRoundedIcon />
                </Box>

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      color: designTokens.text.primary,
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 900,
                    }}
                  >
                    日報
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: designTokens.text.secondary,
                      fontSize: 13,
                    }}
                  >
                    {selectedView === "list"
                      ? "登録済みの日報を確認します。"
                      : selectedView === "create"
                        ? "顧客区分を選択して営業活動を登録します。"
                        : "日報に紐付ける案件を管理します。"}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                size="small"
                label={
                  selectedView === "list"
                    ? "日報一覧"
                    : selectedView === "create"
                      ? "日報登録"
                      : "案件管理"
                }
                sx={{
                  color: designTokens.brand.dark,
                  backgroundColor: designTokens.overlay.brandSoft,
                  border: `1px solid ${designTokens.overlay.brandMedium}`,
                  fontWeight: 800,
                }}
              />
            </Stack>

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderTop: `1px solid ${designTokens.border.default}`,
                backgroundColor: "rgba(255,255,255,0.72)",
                overflowX: "auto",
              }}
            >
              <Tabs
                value={selectedView}
                onChange={(_, value: ReportView) => updateUrl(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="日報機能"
                sx={{
                  minHeight: 56,
                  px: { xs: 0.5, md: 1 },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                    backgroundColor: designTokens.brand.dark,
                  },
                  "& .MuiTab-root": {
                    minHeight: 56,
                    minWidth: { xs: 140, sm: 170 },
                    color: designTokens.text.secondary,
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: 2,
                    mx: 0.25,
                    "&:hover": {
                      color: designTokens.brand.dark,
                      backgroundColor: designTokens.overlay.brandSoft,
                    },
                    "&.Mui-selected": {
                      color: designTokens.brand.dark,
                    },
                  },
                }}
              >
                <Tab
                  value="list"
                  icon={<ListAltRoundedIcon />}
                  iconPosition="start"
                  label="日報一覧"
                />
                <Tab
                  value="create"
                  icon={<EditNoteRoundedIcon />}
                  iconPosition="start"
                  label="日報登録"
                />
                <Tab
                  value="projects"
                  icon={<WorkOutlineRoundedIcon />}
                  iconPosition="start"
                  label="案件管理"
                />
              </Tabs>
            </Box>
          </Paper>

          {selectedView === "list" ? (
            <SalesReportList />
          ) : selectedView === "create" ? (
            <SalesReportForm />
          ) : (
            <ProjectManagement />
          )}
        </Box>
      </Box>
    </Box>
  );
}
